import {Hero as PrimerHero, useWindowSize, useTheme} from '@primer/react-brand'
import {useEffect, useMemo, useRef} from 'react'
import {WebGLDither} from '@github-ui/brand-webgl'
import {getOS, type OS} from '@github-ui/get-os'
import {ssrSafeWindow} from '@github-ui/ssr-utils'

import {ICONS} from './../../svgs'
import type {GitHubAppWaitlistPageCopy, HeroVisual, HeroVisualResolved} from './../../Index.data'

import Styles from './Hero.module.css'

export interface HeroProps {
  label: string
  heading: string
  body: string
  osBadges: GitHubAppWaitlistPageCopy['hero']['osBadges']
  trailingComponent?: React.ReactNode
  cta: {
    label: string
    href: string
  }
  visual: HeroVisual
}

export const Hero = ({label, heading, osBadges, body, trailingComponent, cta, visual}: HeroProps) => {
  const wrapperRef = useRef<HTMLElement>(null)
  const {isLarge: isDesktopView} = useWindowSize()
  const {colorMode} = useTheme()

  const visualSet = useMemo(() => {
    const os = getOS()
    const resolvedOS: OS = os.os === 'macOS' ? 'macOS' : 'Windows'
    const resolvedTheme =
      colorMode === 'auto'
        ? ssrSafeWindow?.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : colorMode

    const result: HeroVisualResolved = {
      desktop: {
        ...visual.desktop,
        src: visual.desktop.src[resolvedOS][resolvedTheme],
      },
      mobile: {
        ...visual.mobile,
        src: visual.mobile.src[resolvedOS][resolvedTheme],
      },
    }
    return result
  }, [visual, colorMode])

  useEffect(() => {
    if (!wrapperRef.current) return

    const wrapper = wrapperRef.current
    const imageContainer: HTMLElement | null = wrapper.querySelector(`.${Styles['Hero__imageContainer']}`)
    const ditherSelector = `.${Styles['Hero__imageDither']}`
    if (!imageContainer) return

    const syncSize = () => {
      const imageDither: HTMLElement | null = wrapper.querySelector(ditherSelector)
      if (!imageDither) return
      const {width, height} = imageContainer.getBoundingClientRect()
      imageDither.style.width = `${width}px`
      imageDither.style.height = `${height}px`
    }

    const resizeObserver = new ResizeObserver(syncSize)
    resizeObserver.observe(imageContainer)

    // The dither element only enters the DOM once `WebGLDither`'s lazy chunk resolves.
    // Watch for it, sync once, then stop observing.
    const mutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement && (node.matches(ditherSelector) || node.querySelector(ditherSelector))) {
            syncSize()
            mutationObserver.disconnect()
            return
          }
        }
      }
    })
    mutationObserver.observe(wrapper, {childList: true, subtree: true})

    syncSize()

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [])

  return (
    <section ref={wrapperRef} className={Styles['Hero']}>
      <PrimerHero
        variant="gridline-expressive"
        enableAnimation
        imageContainerClassName={Styles['Hero__imageContainer']}
        trailingComponent={() => trailingComponent}
      >
        <PrimerHero.Label>{label}</PrimerHero.Label>
        <PrimerHero.Heading>{heading}</PrimerHero.Heading>

        <PrimerHero.Description>
          <span role="list" className={Styles['Hero__osBadges']}>
            {osBadges.map(badge => (
              <span
                key={badge.label}
                role="listitem"
                className={Styles['Hero__osBadges-item']}
                aria-label={badge.label}
              >
                {ICONS[badge.iconName]}
              </span>
            ))}
          </span>

          {body}
        </PrimerHero.Description>
        <PrimerHero.PrimaryAction href={cta.href}>{cta.label}</PrimerHero.PrimaryAction>

        <PrimerHero.Image
          as="picture"
          className={Styles['Hero__image']}
          src={visualSet.desktop.src}
          alt={isDesktopView ? visualSet.desktop.alt : visualSet.mobile.alt}
          sources={[
            {srcset: visualSet.mobile.src, media: '(max-width: 543px)'},
            {srcset: visualSet.desktop.src, media: '(min-width: 544px)'},
          ]}
          enableBorder={false}
          position={isDesktopView ? 'block-end' : 'inline-end-padded'}
        />
      </PrimerHero>

      <WebGLDither
        className={Styles['Hero__imageDither']}
        params={{color: 'default', type: 'wave', fadeIn: true, dotOpacity: 0.2}}
      />
    </section>
  )
}
